/**
 * Forme as the editor's measurer.
 *
 * The canvas normally measures in the browser, which is immediate and free.
 * That is the right default, but it means the editor and the PDF are laid out
 * by two different engines, and where they disagree a page breaks in one place
 * on screen and another in the file. Measuring the canvas with the same engine
 * that renders the PDF removes the disagreement entirely: a break you see is a
 * break you get.
 *
 * The engine is injected rather than imported so this module stays neutral
 * about which Forme entry point the host uses — browser, worker or node.
 */
import type { GeometryMap, RenderTreeOptions, ResolvedNode, SectionGeometry, Theme } from "@broadpaper/core";
import { resolveFonts, type FormeFont } from "./fonts.js";
import { FormeMeasurer, type FormeRenderer } from "./measure.js";

export interface FormeEditorMeasurerOptions {
  /** The engine call, e.g. `renderSerializedDocWithLayout` from `@formepdf/core/browser`. */
  render: FormeRenderer;
  /**
   * Fonts for the engine beyond the theme's own.
   *
   * Usually unnecessary: the faces in `theme.fonts` are read and given to the
   * engine without being named here. May be a promise, because a browser host has
   * to fetch the files and `createMeasurer` has to return synchronously. Every
   * measurement waits for it, so nothing is ever measured — and cached — with the
   * engine's standard faces standing in for a font that was still arriving.
   */
  fonts?: unknown[] | Promise<unknown[]>;
  /** Called for every distinct translation warning, once. */
  onWarning?(message: string): void;
}

/** FNV-1a 32-bit. Fast, and collisions here only cost a re-measure. */
function hash(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36) + s.length.toString(36);
}

/**
 * Measures resolved sections with Forme, caching by section content.
 *
 * Engine measurement is far slower than reading the DOM, so an edit must not
 * re-measure the whole document. Only sections whose content actually changed
 * are sent, and they go in one call rather than one call each.
 */
export class FormeEditorMeasurer {
  private opts: RenderTreeOptions | null = null;
  private readonly cache = new Map<string, SectionGeometry>();
  private readonly seen = new Set<string>();

  constructor(private readonly config: FormeEditorMeasurerOptions) {}

  setOptions(opts: RenderTreeOptions): void {
    this.opts = opts;
  }

  clearCache(): void {
    this.cache.clear();
  }

  destroy(): void {
    this.cache.clear();
  }

  private key(section: ResolvedNode, width: number, mode: string, faces: string): string {
    return hash(JSON.stringify(section)) + ":" + Math.round(width * 100) + ":" + mode + ":" + faces;
  }

  async measure(sections: ResolvedNode[], width: number): Promise<GeometryMap> {
    const opts = this.opts;
    if (!opts) throw new Error("FormeEditorMeasurer.setOptions() must be called before measure()");

    const out: GeometryMap = {};
    const pending: ResolvedNode[] = [];
    const keys = new Map<string, string>();
    // A section measured in one theme's fonts is a different height in another's.
    const faces = facesKey(opts.theme.fonts);
    for (const section of sections) {
      const k = this.key(section, width, opts.mode, faces);
      const hit = this.cache.get(k);
      if (hit) out[section.key] = hit;
      else {
        pending.push(section);
        keys.set(section.key, k);
      }
    }
    if (!pending.length) return out;

    const given = await this.config.fonts;
    const { fonts, warnings } = await resolveFonts({ fonts: given as FormeFont[] | undefined, theme: opts.theme });
    for (const w of warnings) this.warn(w);

    // The measuring page is exactly the content box: giving it the width the
    // caller asked for, with no margins, keeps the two definitions of "width"
    // from ever drifting apart.
    const measurer = new FormeMeasurer({
      registry: opts.registry,
      theme: opts.theme,
      styles: opts.styles,
      format: opts.format,
      mode: opts.mode,
      pageWidth: width,
      margins: { top: 0, right: 0, bottom: 0, left: 0 },
      fonts,
      render: this.config.render
    });

    const measured = await measurer.measure(pending, width);
    for (const [sectionKey, geometry] of Object.entries(measured)) {
      out[sectionKey] = geometry;
      const k = keys.get(sectionKey);
      if (k) this.cache.set(k, geometry);
    }

    for (const w of measurer.warnings) this.warn(w);
    return out;
  }

  private warn(message: string): void {
    if (this.seen.has(message)) return;
    this.seen.add(message);
    this.config.onWarning?.(message);
  }
}

/** Which faces a theme declares, cheaply: a data: URI can be megabytes, so only its ends are read. */
function facesKey(faces: Theme["fonts"]): string {
  if (!faces?.length) return "";
  return hash(
    faces
      .map((f) => [f.family, f.weight, f.style, ...(Array.isArray(f.src) ? f.src : [f.src]).map((s) => `${s.length}:${s.slice(0, 64)}${s.slice(-64)}`)].join("|"))
      .join(";")
  );
}
